<template>
  <div class="plan-page">
    <div class="plan-inner">
      <!-- Hero -->
      <div class="hero">
        <div class="hero-txt">
          <div class="hero-eyebrow">PORTAL HR</div>
          <h1 class="hero-title">Planificare concediu</h1>
          <p class="hero-sub">Planifică-ți perioadele de concediu pentru anul selectat.</p>
        </div>
        <div class="hero-actions">
          <v-btn class="btn-nou" prepend-icon="mdi-plus" @click="deschideAdaugare">
            Adaugă perioadă
          </v-btn>
          <v-select
            v-model="an"
            :items="ani"
            label="An"
            variant="solo"
            density="compact"
            hide-details
            style="max-width:120px"
            class="an-select"
            @update:model-value="onAnChanged"
          />
        </div>
      </div>

      <!-- Dialog rapid adăugare perioadă -->
      <v-dialog v-model="dialog" max-width="440">
        <v-card class="quick-card">
          <div class="quick-head">
            <v-icon color="#ff5722" class="mr-2">mdi-calendar-plus</v-icon>
            <span>Perioadă de concediu</span>
          </div>
          <v-card-text class="pt-4">
            <v-text-field v-model="form.DataInceput" type="date" label="De la" variant="outlined" density="comfortable" hide-details class="mb-3" />
            <v-text-field v-model="form.DataSfarsit" type="date" label="Până la" variant="outlined" density="comfortable" hide-details class="mb-3" />
            <v-textarea v-model="form.Observatie" label="Observație (opțional)" variant="outlined" density="comfortable" rows="2" hide-details auto-grow />
            <div v-if="form.DataInceput && form.DataSfarsit" class="zile-hint">{{ nrZile }} {{ nrZile === 1 ? 'zi' : 'zile' }} planificate</div>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer />
            <v-btn variant="text" @click="dialog = false">Anulează</v-btn>
            <v-btn class="btn-nou" :disabled="!formValid || salving" :loading="salving" @click="salveazaRapid">Salvează</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-progress-linear v-if="loading" indeterminate color="#ff5722" class="mb-2" />

      <div class="scheduler-card">
        <DxScheduler
          ref="schedulerRef"
          :data-source="appointments"
          :current-date="currentDate"
          current-view="month"
          :views="['month']"
          text-expr="Text"
          start-date-expr="StartDate"
          end-date-expr="EndDate"
          all-day-expr="AllDay"
          description-expr="Description"
          recurrence-rule-expr="RecurrenceRule"
          key-expr="ID"
          height="700"
          appointment-template="apptTmpl"
          :editing="{ allowAdding: true, allowUpdating: true, allowDeleting: true, allowResizing: true, allowDragging: true }"
          @appointment-added="onAppointmentAdded"
          @appointment-updated="onAppointmentUpdated"
          @appointment-deleted="onAppointmentDeleted"
        >
          <template #apptTmpl="{ data }">
            <div class="appt-item">{{ data.appointmentData.Text }}</div>
          </template>
        </DxScheduler>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { DxScheduler } from 'devextreme-vue/scheduler'
import PORTAL_PlanificareAnuala from '@/services/Portal/PORTAL_PlanificareAnuala'

export default defineComponent({
  name: 'PlanificareAnuala',
  components: { DxScheduler },
  data() {
    const now = new Date();
    return {
      loading: false,
      an: now.getFullYear(),
      ani: [now.getFullYear(), now.getFullYear() + 1],
      appointments: [] as any[],
      currentDate: new Date(now.getFullYear(), 0, 1),
      dialog: false,
      salving: false,
      form: { DataInceput: '', DataSfarsit: '', Observatie: '' },
    }
  },
  computed: {
    formValid(): boolean {
      return !!this.form.DataInceput && !!this.form.DataSfarsit
        && this.form.DataSfarsit >= this.form.DataInceput;
    },
    nrZile(): number {
      if (!this.form.DataInceput || !this.form.DataSfarsit) return 0;
      const a = new Date(this.form.DataInceput), b = new Date(this.form.DataSfarsit);
      return Math.max(1, Math.round((b.getTime() - a.getTime()) / 86400000) + 1);
    },
  },
  mounted() {
    this.incarca();
  },
  methods: {
    async incarca() {
      this.loading = true;
      try {
        const resp = await PORTAL_PlanificareAnuala.getPlan(this.an);
        this.appointments = resp.data ?? [];
      } catch {
        this.appointments = [];
      } finally {
        this.loading = false;
      }
    },
    onAnChanged() {
      this.currentDate = new Date(this.an, 0, 1);
      this.incarca();
    },
    deschideAdaugare() {
      this.form = { DataInceput: '', DataSfarsit: '', Observatie: '' };
      this.dialog = true;
    },
    async salveazaRapid() {
      if (!this.formValid) return;
      this.salving = true;
      try {
        // AllDay → EndDate exclusiv (adăugăm o zi) ca perioada să acopere și ultima zi în scheduler
        const end = new Date(this.form.DataSfarsit);
        end.setDate(end.getDate() + 1);
        await PORTAL_PlanificareAnuala.create({
          Text: 'Concediu',
          Description: this.form.Observatie,
          StartDate: this.form.DataInceput,
          EndDate: end.toISOString().slice(0, 10),
          AllDay: true,
          RecurrenceRule: null,
        });
        this.dialog = false;
        await this.incarca();
      } catch {
        // TODO: notificare eroare
      } finally {
        this.salving = false;
      }
    },
    async onAppointmentAdded(e: any) {
      try {
        const dto = {
          Text: e.appointmentData.Text,
          Description: e.appointmentData.Description,
          StartDate: e.appointmentData.StartDate,
          EndDate: e.appointmentData.EndDate,
          AllDay: !!e.appointmentData.AllDay,
          RecurrenceRule: e.appointmentData.RecurrenceRule,
        };
        await PORTAL_PlanificareAnuala.create(dto);
      } catch {
        // TODO: notificare eroare
      } finally {
        await this.incarca();
      }
    },
    async onAppointmentUpdated(e: any) {
      try {
        const dto = {
          Text: e.appointmentData.Text,
          Description: e.appointmentData.Description,
          StartDate: e.appointmentData.StartDate,
          EndDate: e.appointmentData.EndDate,
          AllDay: !!e.appointmentData.AllDay,
          RecurrenceRule: e.appointmentData.RecurrenceRule,
        };
        await PORTAL_PlanificareAnuala.update(e.appointmentData.ID, dto);
      } catch {
        // TODO: notificare eroare
      } finally {
        await this.incarca();
      }
    },
    async onAppointmentDeleted(e: any) {
      try {
        await PORTAL_PlanificareAnuala.remove(e.appointmentData.ID);
      } catch {
        // TODO: notificare eroare
      } finally {
        await this.incarca();
      }
    },
  }
})
</script>

<style scoped>
.plan-page { padding: 16px; width: 100%; }
.plan-inner { width: 100%; }

/* Hero */
.hero {
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
  background: linear-gradient(120deg, #ff5722 0%, #ff7a45 55%, #ff9a5c 100%);
  border-radius: 18px; padding: 24px 28px; color: #fff;
  box-shadow: 0 10px 30px rgba(255, 87, 34, .28); margin-bottom: 18px;
}
.hero-eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 2px; opacity: .85; }
.hero-title { font-size: 23px; font-weight: 800; line-height: 1.2; margin: 4px 0 2px; }
.hero-sub { font-size: 13px; opacity: .92; margin: 0; }
.hero-actions { display: flex; align-items: center; gap: 12px; }
.an-select :deep(.v-field) { background: rgba(255, 255, 255, .92); border-radius: 12px; }
.btn-nou { background: #fff !important; color: #ff5722 !important; font-weight: 700; border-radius: 12px; text-transform: none; letter-spacing: 0; }

/* Dialog rapid */
.quick-card { border-radius: 16px; overflow: hidden; }
.quick-head { display: flex; align-items: center; font-weight: 700; color: #1e293b; font-size: 15px; padding: 16px 18px 0; }
.zile-hint { margin-top: 10px; font-size: 12px; font-weight: 700; color: #ff5722; }

/* Scheduler card */
.scheduler-card {
  background: #fff; border: 1px solid #eef1f6; border-radius: 18px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, .05); padding: 16px; overflow: hidden;
}
.appt-item { font-weight: 600; padding: 2px 4px; }

:deep(.dx-scheduler-appointment) {
  background: #ff5722 !important;
  border-color: #ff5722 !important;
}
</style>
